import {
  clearPick,
  isBrandItem,
  MAX_PURCHASE_AGE_DAYS,
  showsMinPremiumHint,
  showsPartnerWindowNotice,
  showsWatchValueHint,
  validateDetails,
  type DetailsField,
  type ValidateContext,
} from "@kovernow/flows/add_item";
import type { PartnerDeclaration } from "@kovernow/types/tenant-web-config";
import { Button } from "@kovernow/react-ui/components/button";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Label } from "@kovernow/react-ui/components/label";
import { RadioGroup, RadioGroupItem } from "@kovernow/react-ui/components/radio-group";
import { Switch } from "@kovernow/react-ui/components/switch";
import { useState } from "react";
import { FormField } from "../../components/FormField";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { STEP_HEADING_ID, StepActions, StepHeading, type StepProps } from "./shared";

const DAY_MS = 86_400_000;

/**
 * Step 3: brand/model/type (locked when picked from search, with Change),
 * Brand/Bespoke for manual entry, purchase value and date, the category
 * hints and, for a matching partner, the tenant's declaration switch.
 */
export function DetailsStep({
  fields,
  setFields,
  goTo,
  back,
  headingRef,
  serverErrors,
  clearServerError,
  ctx,
  declaration,
  onContinue,
  continueLabel,
  editMode = false,
}: StepProps & {
  ctx: ValidateContext;
  declaration: PartnerDeclaration | undefined;
  /** Defaults to the condition step. */
  onContinue?: () => void;
  continueLabel?: string;
  /** Edit (W-6): there is no search step, so a picked item's fields stay locked (no Change). */
  editMode?: boolean;
}) {
  const t = useT();
  const { config } = useServices();
  const [touched, setTouched] = useState<Partial<Record<DetailsField, boolean>>>({});
  const [showAll, setShowAll] = useState(false);
  const errors = validateDetails(fields, ctx);
  const invalid = Object.keys(errors).length > 0;
  const category = fields.category;
  const noun = category ? t(`addItem.noun.${category}`) : "";
  const picked = fields.entry === "picked";
  const branded = isBrandItem(fields);

  const errorFor = (f: DetailsField) => {
    const server = serverErrors[f];
    if (server) return t(server);
    return (showAll || touched[f]) && errors[f] ? t(errors[f]!) : undefined;
  };
  const pickedBrandError = picked
    ? serverErrors.brand
      ? t(serverErrors.brand)
      : errors.brand
        ? t(errors.brand)
        : undefined
    : undefined;
    const blur = (f: DetailsField) => () => setTouched((s) => ({ ...s, [f]: true }));
  const edit = (patch: Partial<typeof fields>, f?: DetailsField) => {
    setFields((cur) => ({ ...cur, ...patch }));
    if (f) clearServerError(f);
  };

  const minDate = new Date(Date.parse(`${ctx.today}T00:00:00Z`) - MAX_PURCHASE_AGE_DAYS * DAY_MS).toISOString().slice(0, 10);
  const hints = [
    showsMinPremiumHint(category) ? t("addItem.hint.minPremium") : null,
    showsWatchValueHint(category) ? t("addItem.hint.watchValue") : null,
  ].filter((h): h is string => !!h);

  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-5" data-testid="step-details">
      <StepHeading headingRef={headingRef}>{t("addItem.details.title")}</StepHeading>

      {picked ? (
        <div className="flex flex-col gap-2 rounded-lg border p-4" data-testid="picked-item">
          <div className="flex items-start justify-between gap-3">
            <dl className="flex min-w-0 flex-col gap-1">
              <dt className="text-sm text-muted-foreground">{t("addItem.details.selected")}</dt>
              <dd className="font-semibold">{[fields.brand, fields.model].filter(Boolean).join(" ")}</dd>
              {fields.type ? <dd className="text-sm text-muted-foreground">{fields.type}</dd> : null}
            </dl>
            {editMode ? null : (
              <Button
                type="button"
                variant="outline"
                size="touch"
                data-testid="change-item"
                aria-describedby={pickedBrandError ? "picked-brand-error" : undefined}
                onClick={() => {
                  setFields((f) => clearPick(f));
                  clearServerError("brand");
                  // Replace: Back from search must not return to a details step the guard bounces.
                  goTo("search", undefined, { replace: true });
                }}
              >
                {t("addItem.details.change")}
              </Button>
            )}
          </div>
          {pickedBrandError && !editMode ? (
            // The locked fields can't be edited here: point the customer to Change.
            <p id="picked-brand-error" className="text-sm font-semibold text-status-error" data-testid="picked-brand-error">
              {pickedBrandError} {t("addItem.details.pickedBrandHint")}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <p id="brand-kind-label" className="font-semibold">
              {t("addItem.details.brandKind")}
            </p>
            <RadioGroup
              aria-labelledby="brand-kind-label"
              value={fields.brandKind}
              onValueChange={(v) => edit({ brandKind: v === "bespoke" ? "bespoke" : "brand" }, "brand")}
              className="flex flex-wrap gap-4"
            >
              {(["brand", "bespoke"] as const).map((k) => (
                <div key={k} className="flex min-h-11 items-center gap-2">
                  <RadioGroupItem value={k} id={`brand-kind-${k}`} />
                  <Label htmlFor={`brand-kind-${k}`}>{t(`addItem.details.${k}`)}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          {branded ? (
            <>
              <FormField
                label={t("addItem.details.brandField")}
                value={fields.brand}
                autoComplete="off"
                required
                error={errorFor("brand")}
                onBlur={blur("brand")}
                onChange={(e) => edit({ brand: e.target.value }, "brand")}
                data-testid="field-brand"
              />
              <FormField
                label={t("addItem.details.model")}
                hint={t("addItem.details.optional")}
                value={fields.model}
                autoComplete="off"
                onChange={(e) => edit({ model: e.target.value })}
                data-testid="field-model"
              />
            </>
          ) : (
            <FormField
              label={t("addItem.details.describe")}
              hint={t("addItem.details.optional")}
              value={fields.notes}
              autoComplete="off"
              onChange={(e) => edit({ notes: e.target.value })}
              data-testid="field-describe"
            />
          )}
          <FormField
            label={t("addItem.details.type")}
            hint={t("addItem.details.optional")}
            value={fields.type}
            autoComplete="off"
            onChange={(e) => edit({ type: e.target.value })}
            data-testid="field-type"
          />
        </>
      )}

      <FormField
        label={t("addItem.details.value", { currency: config.currency })}
        inputMode="decimal"
        autoComplete="off"
        required
        value={fields.value}
        error={errorFor("value")}
        onBlur={blur("value")}
        onChange={(e) => edit({ value: e.target.value }, "value")}
        data-testid="field-value"
      />
      <FormField
        label={t("addItem.details.date")}
        type="date"
        required
        min={minDate}
        max={ctx.today}
        value={fields.date}
        error={errorFor("date")}
        onBlur={blur("date")}
        onChange={(e) => edit({ date: e.target.value }, "date")}
        data-testid="field-date"
      />
      {hints.length ? (
        <div className="flex flex-col gap-1" data-testid="details-hints">
          {hints.map((h) => (
            <p key={h} className="text-sm text-muted-foreground">
              {h}
            </p>
          ))}
        </div>
      ) : null}

      {declaration ? (
        <div className="flex flex-col gap-3" data-testid="partner-declaration">
          <div className="flex min-h-11 items-center justify-between gap-3">
            <Label htmlFor="partner-declaration-switch">{t(declaration.labelKey, { category: noun })}</Label>
            <Switch
              id="partner-declaration-switch"
              checked={fields.isPartnerItem}
              onCheckedChange={(on) => edit({ isPartnerItem: on })}
            />
          </div>
          {showsPartnerWindowNotice(fields, ctx.now) ? (
            <InfoBanner tone="warning" title={t(declaration.windowNoticeKey)} data-testid="partner-window-notice" />
          ) : null}
        </div>
      ) : null}

      <StepActions
        onBack={back}
        invalid={invalid}
        onInvalid={() => setShowAll(true)}
        onContinue={onContinue ?? (() => goTo("condition"))}
        continueLabel={continueLabel}
      />
    </section>
  );
}
